import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eyebrow, SectionHeader, Reveal } from "@/components/SectionHeader";
import { ArrowUpRight } from "lucide-react";

export default function About() {
  return (
    <div>
      {/* HERO */}
      <section className="pt-40 pb-16 md:pb-24 bg-[color:var(--p10-ivory)] border-b border-[color:var(--p10-line)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Eyebrow>About Pay10x</Eyebrow>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.98] mt-6 max-w-5xl"
          >
            The bridge between your systems <br />
            <em className="text-[color:var(--p10-royal)]">and the networks that move value.</em>
          </motion.h1>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>Who we are</Eyebrow>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <p className="text-2xl md:text-3xl font-serif-display leading-[1.25] text-[color:var(--p10-ink)]">
                Pay10x is a Singapore-based payments technology company that connects
                businesses to the global payment networks that move money across borders.
              </p>
              <p className="mt-8 text-lg text-[color:var(--p10-ink)]/70 leading-relaxed max-w-3xl">
                We are an integration and orchestration partner — not a bank, not an e-money
                issuer, and not a custodian of client funds. Our role is to be the secure,
                reliable bridge between your systems and the networks that actually move
                value: translating instructions, routing messages, and delivering real-time
                settlement visibility.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OUR MODEL */}
      <section className="py-24 md:py-32 bg-[color:var(--p10-ink)] text-white relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
          <SectionHeader
            dark
            eyebrow="Our model"
            title={<>Technology and <em className="text-[color:var(--p10-warm)]">connectivity</em> — not custody.</>}
            intro="We operate at the technology and connectivity layer of cross-border payments. Pay10x integrates with global payment networks under formal partner and API arrangements, then exposes that connectivity to our own clients through our proprietary endpoint APIs."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              "We do not hold, safeguard, or take custody of payment funds.",
              "We do not act as principal in any transaction.",
              "We provide the secure messaging, orchestration and status infrastructure connecting clients to regulated networks worldwide.",
            ].map((line, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.7 }}
                className="p-10 bg-[color:var(--p10-ink)]"
              >
                <div className="font-mono-tech text-[color:var(--p10-electric)] text-xs tracking-[0.2em]">
                  0{i + 1}
                </div>
                <p className="mt-6 font-serif-display text-2xl leading-tight text-white">
                  {line}
                </p>
              </motion.div>
            ))}
          </div>
          <Reveal className="mt-16 max-w-3xl">
            <p className="text-white/70 text-lg leading-relaxed">
              This model lets us move fast, integrate broadly, and stay focused on what we do
              best: making global connectivity simple, secure, and reliable — while the
              settlement of funds is handled by the licensed, regulated institutions that
              operate each network.
            </p>
          </Reveal>
        </div>
      </section>

      {/* NETWORK PARTNERSHIPS */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow>Our approach</Eyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-6 leading-[1.02]">
              Intentionally built for <em className="text-[color:var(--p10-royal)]">more than one</em> network.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-4">
            <Reveal>
              <p className="text-lg text-[color:var(--p10-ink)]/75 leading-relaxed">
                Pay10x evaluates and integrates with leading cross-border payment
                infrastructure providers based on corridor coverage, currency reach, payout
                methods, reliability, and compliance standards — and we continue to expand
                this network of partners over time.
              </p>
              <p className="mt-6 text-lg text-[color:var(--p10-ink)]/75 leading-relaxed">
                Our clients benefit from that flexibility without having to manage multiple
                relationships themselves.
              </p>
              <Link data-testid="about-partnerships-link" to="/partnerships" className="btn-square btn-secondary mt-10 inline-flex group">
                See our partnerships approach
                <ArrowUpRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="py-24 md:py-32 bg-white border-y border-[color:var(--p10-line)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Eyebrow>Our story</Eyebrow>
              <img
                src="https://images.unsplash.com/photo-1624003974266-7cdbf877ec00?w=1200&q=80"
                alt="Singapore skyline"
                className="mt-10 w-full h-[420px] object-cover"
              />
            </div>
            <div className="lg:col-span-7 lg:pt-4">
              <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.02]">
                Founded to remove the friction that comes with <em className="text-[color:var(--p10-royal)]">global payments</em>.
              </h2>
              <Reveal className="mt-8">
                <p className="text-lg text-[color:var(--p10-ink)]/75 leading-relaxed">
                  Pay10x was founded to solve a problem every growing fintech, bank, and
                  platform runs into: global payments require connecting to many different
                  networks, each with its own APIs, formats, and onboarding processes.
                </p>
                <p className="mt-6 text-lg text-[color:var(--p10-ink)]/75 leading-relaxed">
                  We built Pay10x to be the single integration layer that removes that
                  friction — starting from Singapore, a natural hub for cross-border payments
                  innovation, and built from day one for global scale.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.02] max-w-2xl">
            Ready to see what a <em className="text-[color:var(--p10-royal)]">single connection</em> can do?
          </h2>
          <div className="flex gap-3">
            <Link data-testid="about-cta-contact" to="/contact" className="btn-square btn-primary">Get in touch</Link>
            <Link data-testid="about-cta-demo" to="/contact" className="btn-square btn-secondary">Book a demo</Link>
          </div>
        </div>
      </section>
    </div>
  );
}